import React, { useCallback, useEffect, useState } from "react";
import { ownerApi, buildFileUrl, errMsg } from "../lib/api";
import { usePubSocket } from "../lib/usePubSocket";
import StaffLoginGate from "../components/StaffLoginGate";
import { RevenuePanel, AlertsPanel } from "../components/StaffPanels";
import PubSettings, { Field, LogoInput } from "../components/OwnerPubSettings";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { DhunLogo, AivigoFooter } from "../components/DhunLogo";
import { toast } from "sonner";
import { LogOut, ArrowLeft, Download, Send } from "lucide-react";

const inp = "h-10 border-white/10 bg-black/30 text-white";
const card = "rounded-2xl border border-white/10 bg-[#131822] p-5 space-y-3";
const APP_URL = window.location.origin;

function PubSelect({ pubs, value, onChange, testid }) {
  return (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger className={`${inp} w-64`} data-testid={testid}><SelectValue placeholder="Choose a pub…" /></SelectTrigger>
      <SelectContent className="border-white/10 bg-[#131822] text-white">
        {pubs.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function SendMessage({ pubs }) {
  const [pubId, setPubId] = useState("");
  const [msg, setMsg] = useState("");
  const send = async () => {
    if (!pubId || !msg.trim()) return toast.error("Pick a pub and write a message");
    try {
      await ownerApi.post("/owner/messages", { pub_id: pubId, message: msg });
      setMsg("");
      toast.success("Message sent");
    } catch (e) {
      toast.error(errMsg(e, "Send failed"));
    }
  };
  return (
    <div className={card} data-testid="owner-send-message">
      <p className="font-bold">Send a message to one pub</p>
      <PubSelect pubs={pubs} value={pubId} onChange={setPubId} testid="owner-message-pub" />
      <Textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={2} className="border-white/10 bg-black/30 text-white" data-testid="owner-message-input" />
      <Button onClick={send} className="bg-violet-500" data-testid="owner-message-send"><Send className="mr-1 h-3 w-3" /> Send</Button>
    </div>
  );
}

const EMPTY = { name: "", slug: "", pub_code: "", logo_url: null, admin_email: "", razorpay_key_id: "", razorpay_key_secret: "", razorpay_webhook_secret: "", admin_password: "", dj_email: "", dj_password: "", commission_pct: 10 };

function AddPub({ onAdded }) {
  const [f, setF] = useState(EMPTY);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    try {
      await ownerApi.post("/owner/pubs", { ...f, admin_email: f.admin_email || null, dj_email: f.dj_email || null, razorpay_key_id: f.razorpay_key_id || null, razorpay_key_secret: f.razorpay_key_secret || null, razorpay_webhook_secret: f.razorpay_webhook_secret || null, commission_pct: Number(f.commission_pct) });
      toast.success("Pub created");
      setF(EMPTY);
      onAdded();
    } catch (e) {
      toast.error(errMsg(e, "Could not create pub"));
    }
  };
  return (
    <div className={card} data-testid="owner-add-pub">
      <p className="font-bold">Add pub</p>
      <div className="grid gap-3 md:grid-cols-2">
        <Field label="Name"><Input className={inp} value={f.name} onChange={set("name")} data-testid="add-pub-name" /></Field>
        <Field label="Slug (URL)"><Input className={inp} value={f.slug} onChange={set("slug")} placeholder="my-pub" data-testid="add-pub-slug" /></Field>
        <Field label="Pub code (min 6)"><Input type="password" autoComplete="off" className={inp} value={f.pub_code} onChange={set("pub_code")} data-testid="add-pub-code" /></Field>
        <Field label="Admin OTP email (blank = owner email)"><Input className={inp} value={f.admin_email} onChange={set("admin_email")} data-testid="add-pub-admin-email" /></Field>
        <Field label="Admin password (min 6)"><Input type="password" className={inp} value={f.admin_password} onChange={set("admin_password")} data-testid="add-pub-admin-password" /></Field>
        <Field label="DJ OTP email (blank = owner email)"><Input className={inp} value={f.dj_email} onChange={set("dj_email")} data-testid="add-pub-dj-email" /></Field>
        <Field label="DJ password (min 6)"><Input type="password" className={inp} value={f.dj_password} onChange={set("dj_password")} data-testid="add-pub-dj-password" /></Field>
        <Field label="Commission %"><Input type="number" className={inp} value={f.commission_pct} onChange={set("commission_pct")} data-testid="add-pub-commission" /></Field>
        <Field label="Razorpay Key ID (optional)"><Input className={inp} autoComplete="off" value={f.razorpay_key_id} onChange={set("razorpay_key_id")} placeholder="rzp_live_…" data-testid="add-pub-rzp-key-id" /></Field>
        <Field label="Razorpay Key Secret (optional)"><Input type="password" autoComplete="new-password" className={inp} value={f.razorpay_key_secret} onChange={set("razorpay_key_secret")} data-testid="add-pub-rzp-key-secret" /></Field>
        <Field label="Razorpay Webhook Secret (optional)"><Input type="password" autoComplete="new-password" className={inp} value={f.razorpay_webhook_secret} onChange={set("razorpay_webhook_secret")} data-testid="add-pub-rzp-webhook-secret" /></Field>
        <Field label="Logo"><LogoInput value={f.logo_url} onChange={(u) => setF({ ...f, logo_url: u })} testid="add-pub-logo" /></Field>
      </div>
      <Button onClick={submit} className="bg-gradient-to-r from-violet-500 to-pink-500" data-testid="add-pub-submit">Create pub</Button>
    </div>
  );
}

function QrCard({ pub }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    ownerApi.get(`/owner/pubs/${pub.id}/qr`, { responseType: "blob" }).then(({ data }) => {
      url = URL.createObjectURL(data);
      setSrc(url);
    }).catch(() => {});
    return () => url && URL.revokeObjectURL(url);
  }, [pub.id]);
  return (
    <div className={`${card} flex flex-col items-center`} data-testid="pub-qr-card">
      {src && <img src={src} alt="QR" className="h-44 w-44 rounded-lg bg-white p-2" data-testid="pub-qr-image" />}
      <DhunLogo size={64} glow={false} />
      <p className="text-sm font-black">Aivigo · {pub.name}</p>
      <p className="text-xs text-slate-400">{APP_URL}/pub/{pub.slug}</p>
      {src && (
        <a href={src} download={`aivigo-${pub.slug}-qr.png`} data-testid="pub-qr-download"
          className="inline-flex items-center gap-1 rounded-full bg-violet-500 px-4 py-2 text-xs font-bold hover:bg-violet-600">
          <Download className="h-3 w-3" /> Download QR
        </a>
      )}
    </div>
  );
}

function PubDetail({ pub, tick, onBack, reload, onDeleted }) {
  return (
    <div className="space-y-6" data-testid="owner-pub-detail">
      <button onClick={onBack} className="text-sm text-slate-400 hover:text-white" data-testid="owner-pub-back"><ArrowLeft className="mr-1 inline h-3 w-3" /> All pubs</button>
      <div className="flex items-center gap-3">
        {pub.logo_url && <img src={buildFileUrl(pub.logo_url)} alt="" className="h-12 w-12 rounded-xl object-cover" />}
        <h2 className="text-2xl font-black" data-testid="owner-pub-detail-name">{pub.name}</h2>
        {!pub.active && <Badge className="bg-pink-500/20 text-pink-200">Inactive</Badge>}
      </div>
      <Tabs defaultValue="revenue">
        <TabsList className="border border-white/10 bg-[#131822]">
          <TabsTrigger value="revenue" data-testid="pub-detail-tab-revenue">Revenue</TabsTrigger>
          <TabsTrigger value="alerts" data-testid="pub-detail-tab-alerts">Alerts</TabsTrigger>
          <TabsTrigger value="settings" data-testid="pub-detail-tab-settings">Settings</TabsTrigger>
          <TabsTrigger value="qr" data-testid="pub-detail-tab-qr">QR code</TabsTrigger>
        </TabsList>
        <TabsContent value="revenue" className="mt-5"><RevenuePanel client={ownerApi} pubId={pub.id} tick={tick} /></TabsContent>
        <TabsContent value="alerts" className="mt-5"><AlertsPanel client={ownerApi} pubId={pub.id} tick={tick} /></TabsContent>
        <TabsContent value="settings" className="mt-5"><PubSettings pub={pub} onSaved={reload} onDeleted={onDeleted} /></TabsContent>
        <TabsContent value="qr" className="mt-5 max-w-sm"><QrCard pub={pub} /></TabsContent>
      </Tabs>
    </div>
  );
}

function OwnerPassword({ onChanged }) {
  const [f, setF] = useState({ current_password: "", new_email: "", new_password: "", new_code: "" });
  const [email, setEmail] = useState("");
  useEffect(() => { ownerApi.get("/owner/account").then(({ data }) => setEmail(data.email)).catch(() => {}); }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    try {
      const body = Object.fromEntries(Object.entries(f).map(([k, v]) => [k, v.trim() ? v : null]));
      await ownerApi.post("/owner/account", body);
      toast.success("Owner login updated. Please sign in again.");
      onChanged();
    } catch (e) {
      toast.error(errMsg(e, "Change failed"));
    }
  };
  return (
    <div className={`${card} max-w-md`} data-testid="owner-password-card">
      <p className="font-bold">Owner login</p>
      <p className="text-xs text-slate-400">Current email: <span className="text-slate-200" data-testid="owner-current-email">{email}</span>. Leave a field blank to keep it.</p>
      <Field label="New email"><Input className={inp} value={f.new_email} onChange={set("new_email")} data-testid="owner-new-email" /></Field>
      <Field label="New password (min 6)"><Input type="password" className={inp} value={f.new_password} onChange={set("new_password")} data-testid="owner-new-password" /></Field>
      <Field label="New owner code (min 6)"><Input type="password" autoComplete="off" className={inp} value={f.new_code} onChange={set("new_code")} data-testid="owner-new-code" /></Field>
      <Field label="Current password (required)"><Input type="password" className={inp} value={f.current_password} onChange={set("current_password")} data-testid="owner-current-password" /></Field>
      <Button onClick={save} className="bg-violet-500" data-testid="owner-password-save">Save changes</Button>
    </div>
  );
}

function OwnerHome({ logout }) {
  const [pubs, setPubs] = useState([]);
  const [tab, setTab] = useState("revenue");
  const [selected, setSelected] = useState(null);
  const [settingsPub, setSettingsPub] = useState("");
  const [tick, setTick] = useState(0);
  const token = localStorage.getItem("dd_owner_token");

  const load = useCallback(() => ownerApi.get("/owner/pubs").then(({ data }) => setPubs(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  usePubSocket("__all__", useCallback((m) => {
    if (["alert", "revenue_update"].includes(m.type)) setTick((t) => t + 1);
    if (m.type === "pubs_update") load();
  }, [load]), token);

  const openPub = (id) => { setSelected(id); setTab("pubs"); };
  const selectedPub = pubs.find((p) => p.id === selected);
  const settingsTarget = pubs.find((p) => p.id === settingsPub);

  return (
    <div className="min-h-screen bg-[#0B0E14] text-white" data-testid="owner-dashboard">
      <header className="border-b border-white/5">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <DhunLogo size={40} />
            <div>
              <p className="text-[10px] uppercase tracking-widest text-violet-300">Owner console</p>
              <p className="text-lg font-extrabold">{pubs.length} pubs</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={logout} data-testid="owner-logout-button" className="border border-white/10"><LogOut className="mr-1 h-3 w-3" /> Sign out</Button>
        </div>
      </header>
      <div className="mx-auto max-w-7xl px-6 py-6">
        <Tabs value={tab} onValueChange={(v) => { setTab(v); if (v !== "pubs") setSelected(null); }}>
          <TabsList className="border border-white/10 bg-[#131822]">
            {["revenue", "alerts", "pubs", "settings"].map((t) => (
              <TabsTrigger key={t} value={t} className="capitalize" data-testid={`owner-tab-${t}`}>{t}</TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="revenue" className="mt-5"><RevenuePanel client={ownerApi} tick={tick} onPubClick={(r) => openPub(r.pub_id)} /></TabsContent>
          <TabsContent value="alerts" className="mt-5 space-y-5">
            <SendMessage pubs={pubs} />
            <AlertsPanel client={ownerApi} tick={tick} showPub />
          </TabsContent>
          <TabsContent value="pubs" className="mt-5">
            {selectedPub ? (
              <PubDetail pub={selectedPub} tick={tick} onBack={() => setSelected(null)} reload={load} onDeleted={() => { setSelected(null); load(); }} />
            ) : (
              <div className="space-y-5">
                <div className="grid gap-3 md:grid-cols-3">
                  {pubs.map((p) => (
                    <button key={p.id} onClick={() => setSelected(p.id)} data-testid={`owner-pub-card-${p.slug}`}
                      className="flex items-center gap-3 rounded-2xl border border-white/10 bg-[#131822] p-4 text-left transition-colors hover:border-violet-400/60">
                      {p.logo_url ? <img src={buildFileUrl(p.logo_url)} alt="" className="h-10 w-10 rounded-lg object-cover" /> : <div className="h-10 w-10 rounded-lg bg-white/5" />}
                      <div className="min-w-0">
                        <p className="truncate font-bold">{p.name}</p>
                        <p className="truncate text-xs text-slate-400">/pub/{p.slug} · {p.commission_pct}%</p>
                      </div>
                      {!p.active && <Badge className="ml-auto bg-pink-500/20 text-pink-200">Inactive</Badge>}
                    </button>
                  ))}
                </div>
                <AddPub onAdded={load} />
              </div>
            )}
          </TabsContent>
          <TabsContent value="settings" className="mt-5 space-y-5">
            <PubSelect pubs={pubs} value={settingsPub} onChange={setSettingsPub} testid="owner-settings-pub" />
            {settingsTarget && <PubSettings pub={settingsTarget} onSaved={load} />}
            <OwnerPassword onChanged={logout} />
          </TabsContent>
        </Tabs>
        <AivigoFooter className="mt-12" />
      </div>
    </div>
  );
}

export default function Owner() {
  const [authed, setAuthed] = useState(() => !!localStorage.getItem("dd_owner_token"));
  useEffect(() => {
    const onUnauth = () => setAuthed(false);
    window.addEventListener("dd:owner-unauth", onUnauth);
    return () => window.removeEventListener("dd:owner-unauth", onUnauth);
  }, []);
  const logout = () => {
    localStorage.removeItem("dd_owner_token");
    setAuthed(false);
  };
  if (!authed) return <StaffLoginGate role="owner" heading="Owner login" onLogin={() => setAuthed(true)} />;
  return <OwnerHome logout={logout} />;
}
